import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import clsx from 'clsx'
import Caption from '../../../../LuxKit/Typography/Caption'
import { endOfMonth } from 'lib/datetime/dateUtils'
import { fillArray } from 'lib/array/arrayUtils'
import noop from 'lib/function/noop'
import DatePickerDay from './DatePickerDay'

const CalendarContainer = styled.div`
  > * + * {
    margin-top: ${rem(4)};
  }
`

const Calendar = styled.div`
  display: grid;
  grid-template-columns: repeat(7 , 1fr);
  align-content: center;
  justify-content: center;
  text-align: center;

  &.fixed-height {
    grid-template-rows: repeat(6, 1fr);
  }
`

const WeekHeader = styled(Caption)`
  display: grid;
  grid-template-columns: repeat(7 , 1fr);
`

const EmptyDay = styled.div`
  min-height: ${rem(50)};
`

interface Props {
  className?: string
  // month and year this calendar is for
  month: number
  year: number
  onDayClick?: (day: Date) => void
  dayRender?: (day: Date) => React.ReactNode
  fixedHeight?: boolean
}

function BasicCalendar(props: Props) {
  const intl = useIntl()
  const {
    className,
    month,
    year,
    dayRender,
    onDayClick = noop,
    fixedHeight = true,
  } = props

  const [daysInMonth, emptyDays] = useMemo(() => {
    const monthDate = new Date(year, month)
    return [
      fillArray(endOfMonth(monthDate).getDate(), day => new Date(year, month, day + 1)),
      fillArray(monthDate.getDay()),
    ]
  }, [month, year])

  const sundayFull = intl.formatMessage({
    id: '86XgxHoWJg',
    defaultMessage: 'Sunday',
    description: 'Full name of Sunday for calendar day header',
  })
  const mondayFull = intl.formatMessage({
    id: 'sN0tV6JP8M',
    defaultMessage: 'Monday',
    description: 'Full name of Monday for calendar day header',
  })
  const tuesdayFull = intl.formatMessage({
    id: 'wdweZoPFYt',
    defaultMessage: 'Tuesday',
    description: 'Full name of Tuesday for calendar day header',
  })
  const wednesdayFull = intl.formatMessage({
    id: 'drs0Y6Hs4k',
    defaultMessage: 'Wednesday',
    description: 'Full name of Wednesday for calendar day header',
  })
  const thursdayFull = intl.formatMessage({
    id: 'i4wgHqMtn7',
    defaultMessage: 'Thursday',
    description: 'Full name of Thursday for calendar day header',
  })
  const fridayFull = intl.formatMessage({
    id: '/0kE3aTaL0',
    defaultMessage: 'Friday',
    description: 'Full name of Friday for calendar day header',
  })
  const saturdayFull = intl.formatMessage({
    id: 'mUcjg4lpjN',
    defaultMessage: 'Saturday',
    description: 'Full name of Saturday for calendar day header',
  })
  const sundayAbbr = intl.formatMessage({
    id: 'EL4rASW4Cn',
    defaultMessage: 'S',
    description: 'Single letter abbreviation for Sunday in calendar',
  })
  const mondayAbbr = intl.formatMessage({
    id: 'AAInP7pFB0',
    defaultMessage: 'M',
    description: 'Single letter abbreviation for Monday in calendar',
  })
  const tuesdayAbbr = intl.formatMessage({
    id: 'ROBqIJNglw',
    defaultMessage: 'T',
    description: 'Single letter abbreviation for Tuesday in calendar',
  })
  const wednesdayAbbr = intl.formatMessage({
    id: 'CHAZnvFqTF',
    defaultMessage: 'W',
    description: 'Single letter abbreviation for Wednesday in calendar',
  })
  const thursdayAbbr = intl.formatMessage({
    id: 'OZqJcTEyHp',
    defaultMessage: 'T',
    description: 'Single letter abbreviation for Thursday in calendar',
  })
  const fridayAbbr = intl.formatMessage({
    id: 'qVFLABr6Xp',
    defaultMessage: 'F',
    description: 'Single letter abbreviation for Friday in calendar',
  })
  const saturdayAbbr = intl.formatMessage({
    id: 'ggcieMMmwo',
    defaultMessage: 'S',
    description: 'Single letter abbreviation for Saturday in calendar',
  })

  return (
    <CalendarContainer className={className}>
      <WeekHeader variant="medium" align="center" colour="neutral-three">
        <abbr title={sundayFull}>{sundayAbbr}</abbr>
        <abbr title={mondayFull}>{mondayAbbr}</abbr>
        <abbr title={tuesdayFull}>{tuesdayAbbr}</abbr>
        <abbr title={wednesdayFull}>{wednesdayAbbr}</abbr>
        <abbr title={thursdayFull}>{thursdayAbbr}</abbr>
        <abbr title={fridayFull}>{fridayAbbr}</abbr>
        <abbr title={saturdayFull}>{saturdayAbbr}</abbr>
      </WeekHeader>
      <Calendar className={clsx({ 'fixed-height': fixedHeight })}>
        {emptyDays.map((val) => <EmptyDay key={val + 50} />)}
        {daysInMonth.map((day) => dayRender?.(day) ?? <DatePickerDay key={day.getTime()} date={day} onClick={onDayClick} />)}
      </Calendar>
    </CalendarContainer>
  )
}

export default BasicCalendar
